/**
 * Shared GHG inventory calculation entry point.
 *
 * The ClimatePlans GHG page and the on-demand inventory report both call
 * `fetchGHGInventoryData`. This module re-exports that function and adds
 * read-only extractors for the already-calculated Chart.js result. It does
 * not recalculate emissions.
 */
import type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';

export { fetchGHGInventoryData, getGhgInventoryFetchStarts, resetGhgInventoryFetchStarts } from '../components/charts/GHGInventorySupabase';
export type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';

/** Chart dataset labels written by fetchGHGInventoryData. */
export const LIVE_CHART_SECTOR_LABELS = {
  LDV: 'Light Duty Vehicles',
  HDV: 'Heavy Duty Vehicles',
  ELEC_RES: 'Electricity Residential',
  ELEC_COM: 'Electricity Commercial',
  NG_RES: 'Natural Gas Residential',
  NG_COM: 'Natural Gas Commercial',
  WASTE: 'Waste & wastewater',
  OFFROAD: 'Offroad',
  AGRICULTURE: 'Agriculture',
} as const;

export type LiveChartSectorLabel = (typeof LIVE_CHART_SECTOR_LABELS)[keyof typeof LIVE_CHART_SECTOR_LABELS];

export function isWasteInventoryLabel(label: string | null | undefined): boolean {
  return label === LIVE_CHART_SECTOR_LABELS.WASTE || label === 'Waste';
}

export interface GhgInventoryChartDataset {
  label?: string;
  data?: Array<number | null | undefined>;
}

export interface ExtractedYearPoint {
  year: number;
  totalMt: number;
  byLabel: Record<string, number>;
}

function chartYears(result: GHGInventoryResult): number[] {
  const labels = (result.chartData?.labels ?? []) as Array<number | string>;
  return labels.map((y) => Number(y)).filter((y) => Number.isFinite(y));
}

function datasets(result: GHGInventoryResult): GhgInventoryChartDataset[] {
  return (result.chartData?.datasets ?? []) as GhgInventoryChartDataset[];
}

/** Read one already-calculated sector value for a year. Returns null if the year or dataset is absent. */
export function extractSectorValue(
  result: GHGInventoryResult,
  chartLabel: string,
  year: number
): number | null {
  const years = chartYears(result);
  const idx = years.indexOf(year);
  if (idx < 0) return null;
  const ds = datasets(result).find((d) => d.label === chartLabel);
  if (!ds || !ds.data || idx >= ds.data.length) return null;
  const n = Number(ds.data[idx]);
  return Number.isFinite(n) ? n : null;
}

/** Sum of already-calculated sector values for a year (same composition as the GHG page stack). */
export function extractYearTotal(result: GHGInventoryResult, year: number): number | null {
  const years = chartYears(result);
  const idx = years.indexOf(year);
  if (idx < 0) return null;
  let sum = 0;
  let any = false;
  for (const ds of datasets(result)) {
    const n = Number(ds.data?.[idx]);
    if (Number.isFinite(n)) {
      sum += n;
      any = true;
    }
  }
  return any ? sum : null;
}

export function extractHistoricalTotals(result: GHGInventoryResult): ExtractedYearPoint[] {
  return chartYears(result).map((year) => {
    const byLabel: Record<string, number> = {};
    for (const ds of datasets(result)) {
      const label = ds.label ?? '';
      const n = extractSectorValue(result, label, year);
      if (n != null) byLabel[label] = n;
    }
    return {
      year,
      totalMt: extractYearTotal(result, year) ?? 0,
      byLabel,
    };
  });
}

export function inventoryYearFromResult(result: GHGInventoryResult, preferredYear = 2023): number {
  const years = chartYears(result);
  if (years.includes(preferredYear)) return preferredYear;
  if (result.latestYear != null && years.includes(result.latestYear)) return result.latestYear;
  return years.length ? years[years.length - 1] : preferredYear;
}
